import { API_URL, User, Role, Permission, SSOProvider, LoginRequest, Token, APIKey, APIKeyCreate, APIKeyCreateResponse, CurrentUserPermissionProfile, AuditLogEntry, BreakGlassIncidentEntry } from '../../types';

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// Authentication
export const login = async (credentials: LoginRequest): Promise<Token> => {
  const response = await fetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  });
  if (!response.ok) throw new Error('Login failed');
  return response.json();
};

export const getCurrentUser = async (): Promise<User> => {
  const response = await fetch(`${API_URL}/api/auth/me`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get current user');
  return response.json();
};

export const getCurrentUserPermissions = async (): Promise<CurrentUserPermissionProfile> => {
  const response = await fetch(`${API_URL}/api/auth/me/permissions`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get current user permissions');
  return response.json();
};

export const checkAuth = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}/api/auth/check`, {
      headers: getAuthHeaders(),
    });
    return response.ok;
  } catch {
    return false;
  }
};

export const logout = () => {
  localStorage.removeItem('auth_token');
  window.location.href = '/login';
};

// SSO
export const getEnabledSSOProviders = async (): Promise<Array<{ provider_id: string; name: string; provider_type: string }>> => {
  const response = await fetch(`${API_URL}/api/auth/sso/providers`);
  if (!response.ok) throw new Error('Failed to get SSO providers');
  return response.json();
};

export const checkSSOConfigured = async (): Promise<{ sso_configured: boolean }> => {
  const response = await fetch(`${API_URL}/api/auth/sso/configured`);
  if (!response.ok) throw new Error('Failed to check SSO configuration');
  return response.json();
};

export const initiateSSOLogin = async (providerId: string): Promise<{ redirect_url: string }> => {
  const response = await fetch(`${API_URL}/api/auth/sso/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider_id: providerId }),
  });
  if (!response.ok) throw new Error('Failed to initiate SSO login');
  return response.json();
};

// SSO Provider Management (Admin)
export const getAllSSOProviders = async (): Promise<SSOProvider[]> => {
  const response = await fetch(`${API_URL}/api/auth/sso-providers/all`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get SSO providers');
  return response.json();
};

export const getSSOProvider = async (providerId: string): Promise<SSOProvider> => {
  const response = await fetch(`${API_URL}/api/auth/sso-providers/${providerId}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get SSO provider');
  return response.json();
};

export const createSSOProvider = async (provider: Partial<SSOProvider>): Promise<SSOProvider> => {
  const response = await fetch(`${API_URL}/api/auth/sso-providers`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(provider),
  });
  if (!response.ok) throw new Error('Failed to create SSO provider');
  return response.json();
};

export const updateSSOProvider = async (providerId: string, updates: Partial<SSOProvider>): Promise<SSOProvider> => {
  const response = await fetch(`${API_URL}/api/auth/sso-providers/${providerId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Failed to update SSO provider');
  return response.json();
};

export const deleteSSOProvider = async (providerId: string): Promise<void> => {
  const response = await fetch(`${API_URL}/api/auth/sso-providers/${providerId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to delete SSO provider');
};

// User Management (Admin)
export const getUsers = async (skip = 0, limit = 100): Promise<User[]> => {
  const response = await fetch(`${API_URL}/api/auth/users?skip=${skip}&limit=${limit}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get users');
  return response.json();
};

export const getUser = async (userId: string): Promise<User> => {
  const response = await fetch(`${API_URL}/api/auth/users/${userId}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get user');
  return response.json();
};

export const createUser = async (user: { username: string; email: string; password: string; first_name?: string; last_name?: string; roles: string[] }): Promise<User> => {
  const response = await fetch(`${API_URL}/api/auth/users`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(user),
  });
  if (!response.ok) throw new Error('Failed to create user');
  return response.json();
};

export const updateUser = async (userId: string, updates: Partial<User>): Promise<User> => {
  const response = await fetch(`${API_URL}/api/auth/users/${userId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Failed to update user');
  return response.json();
};

export const deleteUser = async (userId: string): Promise<void> => {
  const response = await fetch(`${API_URL}/api/auth/users/${userId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to delete user');
};

export const changePassword = async (userId: string, currentPassword: string, newPassword: string): Promise<void> => {
  const response = await fetch(`${API_URL}/api/auth/users/${userId}/password`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!response.ok) throw new Error('Failed to change password');
};

// Role Management (Admin)
export const getRoles = async (skip = 0, limit = 100): Promise<Role[]> => {
  const response = await fetch(`${API_URL}/api/auth/roles?skip=${skip}&limit=${limit}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get roles');
  return response.json();
};

export const getRole = async (roleId: string): Promise<Role> => {
  const response = await fetch(`${API_URL}/api/auth/roles/${roleId}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get role');
  return response.json();
};

export const createRole = async (role: { name: string; description?: string; permissions: string[] }): Promise<Role> => {
  const response = await fetch(`${API_URL}/api/auth/roles`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(role),
  });
  if (!response.ok) throw new Error('Failed to create role');
  return response.json();
};

export const updateRole = async (roleId: string, updates: { description?: string; permissions?: string[] }): Promise<Role> => {
  const response = await fetch(`${API_URL}/api/auth/roles/${roleId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Failed to update role');
  return response.json();
};

export const deleteRole = async (roleId: string): Promise<void> => {
  const response = await fetch(`${API_URL}/api/auth/roles/${roleId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to delete role');
};

// Permission Management
export const getPermissions = async (skip = 0, limit = 100): Promise<Permission[]> => {
  const response = await fetch(`${API_URL}/api/auth/permissions?skip=${skip}&limit=${limit}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get permissions');
  return response.json();
};

// API Key Management (Admin)
export const getAPIKeys = async (): Promise<APIKey[]> => {
  const response = await fetch(`${API_URL}/api/api-keys/`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get API keys');
  return response.json();
};

export const getAPIKey = async (keyId: string): Promise<APIKey> => {
  const response = await fetch(`${API_URL}/api/api-keys/${keyId}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get API key');
  return response.json();
};

export const createAPIKey = async (data: APIKeyCreate): Promise<APIKeyCreateResponse> => {
  const response = await fetch(`${API_URL}/api/api-keys/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to create API key');
  return response.json();
};

export const revokeAPIKey = async (keyId: string): Promise<void> => {
  const response = await fetch(`${API_URL}/api/api-keys/${keyId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to revoke API key');
};

export const getAuditLogs = async (params: {
  skip?: number;
  limit?: number;
  eventType?: string;
  requestId?: string;
  actorUserId?: string;
  path?: string;
} = {}): Promise<AuditLogEntry[]> => {
  const searchParams = new URLSearchParams();
  searchParams.set('skip', String(params.skip ?? 0));
  searchParams.set('limit', String(params.limit ?? 100));
  if (params.eventType) searchParams.set('event_type', params.eventType);
  if (params.requestId) searchParams.set('request_id', params.requestId);
  if (params.actorUserId) searchParams.set('actor_user_id', params.actorUserId);
  if (params.path) searchParams.set('path', params.path);

  const response = await fetch(`${API_URL}/api/auth/audit-logs?${searchParams.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get audit logs');
  return response.json();
};

export const getBreakGlassIncidents = async (params: {
  skip?: number;
  limit?: number;
  q?: string;
  pipelineId?: string;
} = {}): Promise<BreakGlassIncidentEntry[]> => {
  const searchParams = new URLSearchParams();
  searchParams.set('skip', String(params.skip ?? 0));
  searchParams.set('limit', String(params.limit ?? 100));
  if (params.q) searchParams.set('q', params.q);
  if (params.pipelineId) searchParams.set('pipeline_id', params.pipelineId);

  const response = await fetch(`${API_URL}/api/auth/break-glass-incidents?${searchParams.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get break-glass incidents');
  return response.json();
};
